import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement>;

function IconBase({ children, ...props }: IconProps): React.ReactElement {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      {children}
    </svg>
  );
}

export function UploadIcon(props: IconProps): React.ReactElement {
  return (
    <IconBase {...props}>
      <path d="M12 16V3" />
      <path d="m7 8 5-5 5 5" />
      <path d="M5 14v5a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-5" />
    </IconBase>
  );
}

export function CameraIcon(props: IconProps): React.ReactElement {
  return (
    <IconBase {...props}>
      <path d="M14.5 5 13 3h-2L9.5 5H5a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-4.5Z" />
      <circle cx="12" cy="12.5" r="4" />
    </IconBase>
  );
}

export function LockIcon(props: IconProps): React.ReactElement {
  return (
    <IconBase {...props}>
      <rect x="4" y="10" width="16" height="11" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
      <path d="M12 14v3" />
    </IconBase>
  );
}

export function RulerIcon(props: IconProps): React.ReactElement {
  return (
    <IconBase {...props}>
      <path d="m15.5 3.5 5 5-12 12-5-5 12-12Z" />
      <path d="m13 6 2 2M10 9l2 2M7 12l2 2M4 15l2 2" />
    </IconBase>
  );
}

export function PasteIcon(props: IconProps): React.ReactElement {
  return (
    <IconBase {...props}>
      <rect x="7" y="5" width="13" height="16" rx="2" />
      <path d="M4 17V5a2 2 0 0 1 2-2h8" />
      <path d="M10 9h6M10 13h6M10 17h4" />
    </IconBase>
  );
}

export function SparkIcon(props: IconProps): React.ReactElement {
  return (
    <IconBase {...props}>
      <path d="m12 3 1.3 4.1L17 9l-3.7 1.9L12 15l-1.3-4.1L7 9l3.7-1.9L12 3Z" />
      <path d="m19 15 .7 2.2L22 18l-2.3.8L19 21l-.7-2.2L16 18l2.3-.8L19 15Z" />
      <path d="m5 13 .7 2.2L8 16l-2.3.8L5 19l-.7-2.2L2 16l2.3-.8L5 13Z" />
    </IconBase>
  );
}

export function CheckIcon(props: IconProps): React.ReactElement {
  return (
    <IconBase {...props}>
      <path d="m5 12 4 4L19 6" />
    </IconBase>
  );
}

export function AlertIcon(props: IconProps): React.ReactElement {
  return (
    <IconBase {...props}>
      <path d="M12 3 2.7 20h18.6L12 3Z" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </IconBase>
  );
}

export function CopyIcon(props: IconProps): React.ReactElement {
  return (
    <IconBase {...props}>
      <rect x="8" y="8" width="12" height="12" rx="2" />
      <path d="M16 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h3" />
    </IconBase>
  );
}

export function DownloadIcon(props: IconProps): React.ReactElement {
  return (
    <IconBase {...props}>
      <path d="M12 3v12" />
      <path d="m7 10 5 5 5-5" />
      <path d="M4 21h16" />
    </IconBase>
  );
}

export function RefreshIcon(props: IconProps): React.ReactElement {
  return (
    <IconBase {...props}>
      <path d="M20 7V3h-4" />
      <path d="M20 3a9 9 0 1 0 2 9" />
    </IconBase>
  );
}

export function EyeIcon(props: IconProps): React.ReactElement {
  return (
    <IconBase {...props}>
      <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z" />
      <circle cx="12" cy="12" r="2.5" />
    </IconBase>
  );
}

export function EyeOffIcon(props: IconProps): React.ReactElement {
  return (
    <IconBase {...props}>
      <path d="m3 3 18 18" />
      <path d="M10.6 6.2A11.7 11.7 0 0 1 12 6c6.5 0 10 6 10 6a16 16 0 0 1-2.1 2.9" />
      <path d="M6.2 6.2C3.4 8 2 12 2 12s3.5 6 10 6a10.8 10.8 0 0 0 4.1-.8" />
      <path d="M9.9 9.9A3 3 0 0 0 14.1 14" />
    </IconBase>
  );
}

export function CloseIcon(props: IconProps): React.ReactElement {
  return (
    <IconBase {...props}>
      <path d="M6 6l12 12M18 6 6 18" />
    </IconBase>
  );
}

export function ArrowIcon(props: IconProps): React.ReactElement {
  return (
    <IconBase {...props}>
      <path d="M5 12h14" />
      <path d="m14 7 5 5-5 5" />
    </IconBase>
  );
}

export function InfoIcon(props: IconProps): React.ReactElement {
  return (
    <IconBase {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v6" />
      <path d="M12 7h.01" />
    </IconBase>
  );
}

export function MenuIcon(props: IconProps): React.ReactElement {
  return (
    <IconBase {...props}>
      <path d="M4 7h16M4 12h16M4 17h16" />
    </IconBase>
  );
}
